"use client"

import * as React from "react"
import {
  Settings,
  Briefcase,
  FolderOpen,
  UserCheck,
  Sliders,
  PanelRight,
  Grid,
  Puzzle,
  ShoppingBag,
  Wrench,
  Clock,
  History,
  ChevronRight,
  ChevronLeft,
} from "lucide-react"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Account, UserProfile, Language } from "@/types/workspace"

// Sub-Tab Components
import { CompanySettingsTab } from "@/components/settings/tabs/CompanySettingsTab"
import { WorkspacesSettingsTab } from "@/components/settings/tabs/WorkspacesSettingsTab"
import { UsersSettingsTab } from "@/components/settings/tabs/UsersSettingsTab"
import { DeskSettingsTab } from "@/components/settings/tabs/DeskSettingsTab"
import { DockSettingsTab } from "@/components/settings/tabs/DockSettingsTab"
import { BarSettingsTab } from "@/components/settings/tabs/BarSettingsTab"
import { DeveloperSettingsTab } from "@/components/settings/tabs/DeveloperSettingsTab"
import { HistorySettingsTab } from "@/components/settings/tabs/HistorySettingsTab"

export interface SettingsViewProps {
  isMobile: boolean
  settingsTab: string
  setSettingsTab: (tab: string) => void
  t: Record<string, string>
  profileRole: string
  workspaceRole?: string
  currentAccount: Account | null
  handleSaveCompany?: (data?: any) => Promise<any> | void
  companyName: string
  setCompanyName: (val: string) => void
  companyDocument: string
  setCompanyDocument: (val: string) => void
  companyLogoUrl: string
  setCompanyLogoUrl: (val: string) => void
  companyBannerUrl?: string
  setCompanyBannerUrl?: (val: string) => void
  companyPrimaryColor: string
  setCompanyPrimaryColor: (val: string) => void
  companyCustomDomain: string
  setCompanyCustomDomain: (val: string) => void
  companySamlEnabled: boolean
  setCompanySamlEnabled: (val: boolean) => void
  companySamlIdpEntityId: string
  setCompanySamlIdpEntityId: (val: string) => void
  companySamlSsoUrl: string
  setCompanySamlSsoUrl: (val: string) => void
  companySamlCertificate: string
  setCompanySamlCertificate: (val: string) => void
  accounts: Account[]
  activeAccountId: string
  handleSwitchWorkspace: (id: string) => void
  handleCreateWorkspace: (name: string, accountId: string, slug?: string) => Promise<any>
  handleUpdateWorkspace?: (id: string, name: string) => Promise<any>
  handleArchiveWorkspace?: (id: string) => Promise<any>
  handleReactivateWorkspace?: (id: string) => Promise<any>
  handleDeleteWorkspace?: (id: string) => Promise<any>
  usersList: UserProfile[]
  handleInviteUser: (email: string, name: string, role: string, workspaces?: Array<{ workspaceId: string; role: string }>) => Promise<any> | void
  handleUpdateAccountMember?: (userId: string, data: { name?: string; role?: string; status?: string; customRoleId?: string | null }) => Promise<any> | void
  handleUpdateUserRole?: (userId: string, role: string) => void
  handleRemoveUser: (userId: string) => Promise<any> | void
  wallpaperTab: string
  setWallpaperTab: (tab: string) => void
  mobileWallpaper: string
  setMobileWallpaper: (val: string) => void
  desktopWallpaper: string
  setDesktopWallpaper: (val: string) => void
  customWallpapers?: string[]
  setCustomWallpapers?: any
  useSeparateMobileWallpaper: boolean
  setUseSeparateMobileWallpaper: (val: boolean) => void
  handleSaveWallpaperToDb: (d: string, m: string, sep: boolean) => void
  dockPosition: "bottom" | "left" | "right"
  setDockPosition: (pos: "bottom" | "left" | "right") => void
  dockSize: number
  setDockSize: (size: number) => void
  dockAutoHide: boolean
  setDockAutoHide: (hide: boolean) => void
  topBarConfig: any
  setTopBarConfig: any
  installedApps: any[]
  handleToggleAppStatus: (id: string) => void
  handleUninstallApp: (id: string) => void
  handleInstallApp?: (app: any) => void
  handleRenameApp?: (appId: string, newName: string) => void
  availableWidgets: any[]
  setWidgetsConfig?: React.Dispatch<React.SetStateAction<any[]>>
  pinnedWidgets: string[]
  handleToggleWidgetPin: (id: string) => void
  marketplaceApps: any[]
  globallyInstalledApps: string[]
  developerApps: any[]
  setAuthAppToShow: any
  handleToggleGlobalApp: any
  apiKey: string
  handleGenerateApiKey: () => void
  webhookUrl: string
  setWebhookUrl: (url: string) => void
  handleSaveWebhook: () => void
  handleOpenMarketplaceTab?: () => void
  handleOpenApp?: (app: any) => void
  hiddenAppIds?: string[]
  setHiddenAppIds?: React.Dispatch<React.SetStateAction<string[]>>
  dockAppIds?: string[]
  setDockAppIds?: React.Dispatch<React.SetStateAction<string[]>>
  devSubTab?: string
  setDevSubTab?: (subTab: string) => void
  lang: Language
  user?: UserProfile | null
  desktopGridCols?: number
  setDesktopGridCols?: (cols: number) => void
  desktopIconSize?: number
  setDesktopIconSize?: (size: number) => void
  desktopShowLabels?: boolean
  setDesktopShowLabels?: (show: boolean) => void
}

export function SettingsView({
  isMobile,
  settingsTab,
  setSettingsTab,
  t,
  profileRole,
  currentAccount,
  handleSaveCompany,
  companyName,
  setCompanyName,
  companyDocument,
  setCompanyDocument,
  companyLogoUrl,
  setCompanyLogoUrl,
  companyBannerUrl: propCompanyBannerUrl,
  setCompanyBannerUrl: propSetCompanyBannerUrl,
  companyPrimaryColor,
  setCompanyPrimaryColor,
  companyCustomDomain,
  setCompanyCustomDomain,
  companySamlEnabled,
  setCompanySamlEnabled,
  companySamlIdpEntityId,
  setCompanySamlIdpEntityId,
  companySamlSsoUrl,
  setCompanySamlSsoUrl,
  companySamlCertificate,
  setCompanySamlCertificate,
  accounts,
  activeAccountId,
  handleSwitchWorkspace,
  handleCreateWorkspace,
  handleUpdateWorkspace,
  handleArchiveWorkspace,
  handleReactivateWorkspace,
  handleDeleteWorkspace,
  usersList,
  handleInviteUser,
  handleUpdateAccountMember,
  handleUpdateUserRole,
  handleRemoveUser,
  wallpaperTab,
  setWallpaperTab,
  mobileWallpaper,
  setMobileWallpaper,
  desktopWallpaper,
  setDesktopWallpaper,
  customWallpapers,
  setCustomWallpapers,
  useSeparateMobileWallpaper,
  setUseSeparateMobileWallpaper,
  handleSaveWallpaperToDb,
  dockPosition,
  setDockPosition,
  dockSize,
  setDockSize,
  dockAutoHide,
  setDockAutoHide,
  topBarConfig,
  setTopBarConfig,
  installedApps,
  handleToggleAppStatus,
  handleUninstallApp,
  handleInstallApp,
  handleRenameApp,
  availableWidgets,
  setWidgetsConfig,
  pinnedWidgets,
  handleToggleWidgetPin,
  marketplaceApps,
  globallyInstalledApps,
  developerApps,
  setAuthAppToShow,
  handleToggleGlobalApp,
  apiKey,
  handleGenerateApiKey,
  webhookUrl,
  setWebhookUrl,
  handleSaveWebhook,
  handleOpenMarketplaceTab,
  handleOpenApp,
  hiddenAppIds = [],
  setHiddenAppIds,
  dockAppIds,
  setDockAppIds,
  devSubTab = "api-keys",
  setDevSubTab,
  lang,
  user,
  desktopGridCols,
  setDesktopGridCols,
  desktopIconSize,
  setDesktopIconSize,
  desktopShowLabels,
  setDesktopShowLabels,
  workspaceRole = "member",
}: SettingsViewProps) {
  const isCompanyAdmin = profileRole === "admin" || profileRole === "owner"
  const isWorkspaceAdmin = isCompanyAdmin || workspaceRole === "admin"

  React.useEffect(() => {
    if (isMobile && (settingsTab === "dock" || settingsTab === "bar-tab")) {
      setSettingsTab("wallpaper-tab")
    }
    if (settingsTab === "apps-list-tab" || settingsTab === "widgets-list-tab") {
      setSettingsTab(isMobile ? "menu" : "company")
    }
    if (!isCompanyAdmin) {
      const companyAdminOnlyTabs = ["developer", "history"]
      if (companyAdminOnlyTabs.includes(settingsTab)) {
        setSettingsTab("wallpaper-tab")
      }
    }
  }, [isCompanyAdmin, isMobile, settingsTab, setSettingsTab])

  const corporateItems = [
    { id: "company", label: t.settingsTabCompany || "Empresa", desc: "Perfil, identidade e dados institucionais", icon: Briefcase, color: "text-blue-600 dark:text-blue-400 bg-blue-500/10" },
    { id: "workspaces", label: t.settingsTabWorkspaces || "Ambientes", desc: "Gerenciar e alternar ambientes de trabalho", icon: FolderOpen, color: "text-indigo-600 dark:text-indigo-400 bg-indigo-500/10" },
    { id: "users", label: t.settingsTabUsers || "Usuários & Perfis", desc: "Membros, convites e controle de papéis", icon: UserCheck, color: "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10" },
  ]

  const appearanceItems = isMobile
    ? [
      { id: "wallpaper-tab", label: "Desk", desc: "Papéis de parede do computador e celular", icon: Sliders, color: "text-purple-600 dark:text-purple-400 bg-purple-500/10" },
    ]
    : [
      { id: "wallpaper-tab", label: "Desk", desc: "Papéis de parede do computador e celular", icon: Sliders, color: "text-purple-600 dark:text-purple-400 bg-purple-500/10" },
      { id: "dock", label: "Dock", desc: "Posição e comportamento da barra de tarefas", icon: PanelRight, color: "text-cyan-600 dark:text-cyan-400 bg-cyan-500/10" },
      { id: "bar-tab", label: "Bar", desc: "Personalização e atalhos da barra superior", icon: Clock, color: "text-amber-600 dark:text-amber-400 bg-amber-500/10" },
    ]

  const advancedItems = isCompanyAdmin ? [
    { id: "developer", label: t.settingsTabDeveloper || "Integrações & Conexões", desc: "Chaves de API, Webhooks, SAML SSO e MCP", icon: Wrench, color: "text-amber-600 dark:text-amber-400 bg-amber-500/10" },
    { id: "history", label: t.settingsTabHistory || "Registros", desc: "Logs de auditoria e atividades da empresa", icon: History, color: "text-blue-600 dark:text-blue-400 bg-blue-500/10" },
  ] : []

  const allItems = [...corporateItems, ...appearanceItems, ...advancedItems]
  const currentTabInfo = allItems.find(item => item.id === settingsTab)

  return (
    <Tabs
      orientation={isMobile ? "horizontal" : "vertical"}
      value={settingsTab}
      onValueChange={(value: any) => setSettingsTab(value)}
      className="flex flex-col md:flex-row flex-1 h-full overflow-hidden"
    >
      {/* Menu Principal Mobile (Apenas em Mobile quando na raiz de ajustes) */}
      {isMobile && settingsTab === "menu" && (
        <div className="flex-1 w-full bg-zinc-50 dark:bg-zinc-950 flex flex-col h-full overflow-y-auto p-4 pb-28 select-none font-sans">
          <div className="flex items-center gap-3 mb-5 px-1 pt-1">
            <div className="flex size-9 items-center justify-center rounded-xl bg-[#004EB1] shadow-xs shrink-0">
              <Settings className="size-5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="font-bold text-base text-zinc-900 dark:text-white leading-tight">
                {t.settingsTitle || "Ajustes do Sistema"}
              </h2>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 font-normal truncate">
                {currentAccount?.name || "Bold Workspace"}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Corporativo */}
            <div>
              <div className="px-2 mb-1.5 text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">
                {t.settingsCategoryCorporate || "Corporativo"}
              </div>
              <div className="bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800/80 rounded-2xl overflow-hidden shadow-xs divide-y divide-zinc-100 dark:divide-zinc-800/60">
                {corporateItems.map((item) => {
                  const Icon = item.icon
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSettingsTab(item.id)}
                      className="w-full flex items-center justify-between p-3.5 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 active:bg-zinc-100 dark:active:bg-zinc-800 transition-colors text-left cursor-pointer"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`size-8 rounded-xl flex items-center justify-center shrink-0 ${item.color}`}>
                          <Icon className="size-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-bold text-zinc-900 dark:text-zinc-100 truncate">
                            {item.label}
                          </div>
                          <div className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate">
                            {item.desc}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="size-4 text-zinc-400 dark:text-zinc-500 shrink-0 ml-2" />
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Aparência & Interface */}
            <div>
              <div className="px-2 mb-1.5 text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">
                Aparência & Interface
              </div>
              <div className="bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800/80 rounded-2xl overflow-hidden shadow-xs divide-y divide-zinc-100 dark:divide-zinc-800/60">
                {appearanceItems.map((item) => {
                  const Icon = item.icon
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSettingsTab(item.id)}
                      className="w-full flex items-center justify-between p-3.5 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 active:bg-zinc-100 dark:active:bg-zinc-800 transition-colors text-left cursor-pointer"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`size-8 rounded-xl flex items-center justify-center shrink-0 ${item.color}`}>
                          <Icon className="size-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-bold text-zinc-900 dark:text-zinc-100 truncate">
                            {item.label}
                          </div>
                          <div className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate">
                            {item.desc}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="size-4 text-zinc-400 dark:text-zinc-500 shrink-0 ml-2" />
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Avançado & Sistema */}
            {isCompanyAdmin && advancedItems.length > 0 && (
              <div>
                <div className="px-2 mb-1.5 text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">
                  Avançado & Sistema
                </div>
                <div className="bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800/80 rounded-2xl overflow-hidden shadow-xs divide-y divide-zinc-100 dark:divide-zinc-800/60">
                  {advancedItems.map((item) => {
                    const Icon = item.icon
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setSettingsTab(item.id)}
                        className="w-full flex items-center justify-between p-3.5 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 active:bg-zinc-100 dark:active:bg-zinc-800 transition-colors text-left cursor-pointer"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`size-8 rounded-xl flex items-center justify-center shrink-0 ${item.color}`}>
                            <Icon className="size-4" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-bold text-zinc-900 dark:text-zinc-100 truncate">
                              {item.label}
                            </div>
                            <div className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate">
                              {item.desc}
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="size-4 text-zinc-400 dark:text-zinc-500 shrink-0 ml-2" />
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sidebar Esquerda (Apenas no Desktop) */}
      {!isMobile && (
        <div className="w-60 bg-zinc-50 dark:bg-zinc-950 border-r border-zinc-150 dark:border-zinc-800 p-4 flex flex-col justify-between shrink-0 select-none font-sans h-full overflow-y-auto">
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-2 py-1">
              <div className="flex size-7 items-center justify-center rounded bg-[#004EB1]">
                <Settings className="size-4 text-white" />
              </div>
              <span className="font-bold text-xs text-zinc-900 dark:text-white uppercase tracking-wider">
                {t.settingsTitle}
              </span>
            </div>

            <div className="space-y-4 text-[10px]">
              <div className="space-y-1">
                <div className="px-3 text-[9px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">
                  {t.settingsCategoryCorporate}
                </div>
                <TabsList className="flex flex-col gap-0.5 w-full bg-transparent border-0 p-0 h-auto">
                  <TabsTrigger
                    value="company"
                    className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${settingsTab === "company"
                      ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold"
                      : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50"
                      }`}
                  >
                    <Briefcase className="size-4 shrink-0" />
                    {t.settingsTabCompany}
                  </TabsTrigger>

                  <TabsTrigger
                    value="workspaces"
                    className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${settingsTab === "workspaces"
                      ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold"
                      : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50"
                      }`}
                  >
                    <FolderOpen className="size-4 shrink-0" />
                    {t.settingsTabWorkspaces}
                  </TabsTrigger>

                  <TabsTrigger
                    value="users"
                    className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${settingsTab === "users"
                      ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold"
                      : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50"
                      }`}
                  >
                    <UserCheck className="size-4 shrink-0 text-[#004EB1] dark:text-blue-400" />
                    {t.settingsTabUsers}
                  </TabsTrigger>
                </TabsList>
              </div>

              <div className="space-y-1">
                <div className="px-3 text-[9px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">
                  Aparência & Interface
                </div>
                <TabsList className="flex flex-col gap-0.5 w-full bg-transparent border-0 p-0 h-auto">
                  <TabsTrigger
                    value="wallpaper-tab"
                    className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${settingsTab === "wallpaper-tab"
                      ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold"
                      : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50"
                      }`}
                  >
                    <Sliders className="size-4 shrink-0" />
                    Desk
                  </TabsTrigger>

                  <TabsTrigger
                    value="dock"
                    className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${settingsTab === "dock"
                      ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold"
                      : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50"
                      }`}
                  >
                    <PanelRight className="size-4 shrink-0" />
                    Dock
                  </TabsTrigger>

                  <TabsTrigger
                    value="bar-tab"
                    className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${settingsTab === "bar-tab"
                      ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold"
                      : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50"
                      }`}
                  >
                    <Clock className="size-4 shrink-0" />
                    Bar
                  </TabsTrigger>
                </TabsList>
              </div>
            </div>

            {isCompanyAdmin && (
              <div className="pt-2 border-t border-zinc-200 dark:border-zinc-800 space-y-1 select-none">
                <TabsList className="flex flex-col w-full bg-transparent border-0 p-0 h-auto gap-0.5">
                  <TabsTrigger
                    value="developer"
                    className={`flex items-center gap-2.5 w-full px-3 py-2 text-xs font-bold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${settingsTab === "developer"
                      ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold font-sans"
                      : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50 font-sans"
                      }`}
                  >
                    <Wrench className="size-4 shrink-0 text-amber-500 animate-pulse" />
                    {t.settingsTabDeveloper || "Integrações & Conexões"}
                  </TabsTrigger>

                  <TabsTrigger
                    value="history"
                    className={`flex items-center gap-2.5 w-full px-3 py-2 text-xs font-bold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${settingsTab === "history"
                      ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold font-sans"
                      : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50 font-sans"
                      }`}
                  >
                    <History className="size-4 shrink-0" />
                    {t.settingsTabHistory || "Registros"}
                  </TabsTrigger>
                </TabsList>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Painel da Direita (Conteúdo com scroll vertical) */}
      {(!isMobile || settingsTab !== "menu") && (
        <div className="flex-1 min-h-0 bg-white dark:bg-zinc-900 p-6 pb-24 overflow-y-auto max-w-full overflow-x-hidden h-full font-sans">
          {isMobile && (
            <div className="mb-4 pb-3 border-b border-zinc-150 dark:border-zinc-800 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setSettingsTab("menu")}
                className="flex items-center gap-1 text-xs font-bold text-[#004EB1] dark:text-blue-400 bg-blue-500/10 dark:bg-blue-500/20 px-3 py-1.5 rounded-lg w-fit transition-all active:scale-95 cursor-pointer shrink-0"
              >
                <ChevronLeft className="size-4" />
                <span>Ajustes</span>
              </button>
              {currentTabInfo && (
                <div className="flex items-center gap-1.5 text-xs font-bold text-zinc-700 dark:text-zinc-200 truncate">
                  <currentTabInfo.icon className="size-4 text-[#004EB1] dark:text-blue-400 shrink-0" />
                  <span className="truncate">{currentTabInfo.label}</span>
                </div>
              )}
            </div>
          )}
          <div className="flex-1 space-y-6 pb-12">
            {/* 1. Aba Empresa */}
            <CompanySettingsTab
              currentAccount={currentAccount}
              companyName={companyName}
              setCompanyName={setCompanyName}
              companyDocument={companyDocument}
              setCompanyDocument={setCompanyDocument}
              companyLogoUrl={companyLogoUrl}
              setCompanyLogoUrl={setCompanyLogoUrl}
              companyBannerUrl={propCompanyBannerUrl}
              setCompanyBannerUrl={propSetCompanyBannerUrl}
              companyPrimaryColor={companyPrimaryColor}
              setCompanyPrimaryColor={setCompanyPrimaryColor}
              companyCustomDomain={companyCustomDomain}
              setCompanyCustomDomain={setCompanyCustomDomain}
              handleSaveCompany={handleSaveCompany}
              profileRole={profileRole}
              t={t}
            />

            {/* 2. Aba Workspaces */}
            <WorkspacesSettingsTab
              currentAccount={currentAccount}
              handleCreateWorkspace={handleCreateWorkspace}
              handleUpdateWorkspace={handleUpdateWorkspace}
              handleArchiveWorkspace={handleArchiveWorkspace}
              handleReactivateWorkspace={handleReactivateWorkspace}
              handleDeleteWorkspace={handleDeleteWorkspace}
              usersList={usersList}
              t={t}
              profileRole={profileRole}
              user={user}
            />

            {/* 3. Aba Usuários & Perfis */}
            <UsersSettingsTab
              usersList={usersList}
              user={user}
              profileRole={profileRole}
              currentAccount={currentAccount}
              activeAccountId={activeAccountId}
              installedApps={installedApps}
              handleInviteUser={handleInviteUser}
              handleUpdateAccountMember={handleUpdateAccountMember}
              handleUpdateUserRole={handleUpdateUserRole || (() => { })}
              handleRemoveUser={handleRemoveUser}
              t={t}
            />

            {/* 4. Aba Desktop (Wallpaper, Widgets, Grid) */}
            <DeskSettingsTab
              wallpaperTab={wallpaperTab}
              setWallpaperTab={setWallpaperTab}
              mobileWallpaper={mobileWallpaper}
              setMobileWallpaper={setMobileWallpaper}
              desktopWallpaper={desktopWallpaper}
              setDesktopWallpaper={setDesktopWallpaper}
              customWallpapers={customWallpapers}
              setCustomWallpapers={setCustomWallpapers}
              useSeparateMobileWallpaper={useSeparateMobileWallpaper}
              handleSaveWallpaperToDb={handleSaveWallpaperToDb}
              availableWidgets={availableWidgets}
              setWidgetsConfig={setWidgetsConfig}
              desktopGridCols={desktopGridCols}
              setDesktopGridCols={setDesktopGridCols}
              desktopIconSize={desktopIconSize}
              setDesktopIconSize={setDesktopIconSize}
              desktopShowLabels={desktopShowLabels}
              setDesktopShowLabels={setDesktopShowLabels}
              user={user}
            />

            {/* 5. Aba Dock */}
            <DockSettingsTab
              dockPosition={dockPosition}
              setDockPosition={setDockPosition}
              dockSize={dockSize}
              setDockSize={setDockSize}
              dockAutoHide={dockAutoHide}
              setDockAutoHide={setDockAutoHide}
              dockAppIds={dockAppIds}
              setDockAppIds={setDockAppIds}
              installedApps={installedApps}
            />

            {/* 6. Aba Bar (Barra Superior) */}
            <BarSettingsTab
              topBarConfig={topBarConfig}
              setTopBarConfig={setTopBarConfig}
              user={user}
            />

            {/* 7. Aba Desenvolvedor & Integrações */}
            {isCompanyAdmin && (
              <DeveloperSettingsTab
                devSubTab={devSubTab}
                setDevSubTab={setDevSubTab}
                apiKey={apiKey}
                handleGenerateApiKey={handleGenerateApiKey}
                webhookUrl={webhookUrl}
                setWebhookUrl={setWebhookUrl}
                handleSaveWebhook={handleSaveWebhook}
                companySamlEnabled={companySamlEnabled}
                setCompanySamlEnabled={setCompanySamlEnabled}
                companySamlIdpEntityId={companySamlIdpEntityId}
                setCompanySamlIdpEntityId={setCompanySamlIdpEntityId}
                companySamlSsoUrl={companySamlSsoUrl}
                setCompanySamlSsoUrl={setCompanySamlSsoUrl}
                companySamlCertificate={companySamlCertificate}
                setCompanySamlCertificate={setCompanySamlCertificate}
                currentAccount={currentAccount}
                activeAccountId={activeAccountId}
                t={t}
              />
            )}

            {/* 10. Aba Registros de Auditoria (History) */}
            {isCompanyAdmin && (
              <HistorySettingsTab
                currentAccount={currentAccount}
                profileRole={profileRole}
                t={t}
              />
            )}
          </div>
        </div>
      )}
    </Tabs>
  )
}
